import { useCallback, useRef, useState, UIEvent } from 'react';
import { checkScrollStatus } from 'src/components/common/UtilityFunctions';

export interface IPagedResult<T> {
    items: Array<T>;
    totalRecords: number;
}

// Reusable "cap the query, load more as the user scrolls to the bottom" pattern - drop-in
// replacement for the copy/pasted addSkipTop(0, 50)-and-nothing-else pattern this session put in
// several places. Reuses the same scrollTop/scrollHeight/clientHeight math already shared in
// UtilityFunctions.checkScrollStatus (which mirrors FindClientDialog.tsx's own inline
// getScrollStatus) - deliberately NOT the getBoundingClientRect/IntersectionObserver-relative-to-
// window.innerHeight approach in CardContainer.redesign.tsx, which only works for window-level
// scrolling and breaks for content inside its own overflow:scroll container (which every grid this
// session built uses).
export function useInfiniteScroll<T>(fetchPage: (pageNumber: number) => Promise<IPagedResult<T>>, pageSize: number) {
    const [items, setItems] = useState<Array<T>>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    // Mirrors pageNumber/totalRecords/loading synchronously via refs, alongside the state above.
    // reset() immediately followed by loadNextPage() - the normal "start a fresh search" call
    // pattern - needs loadNextPage's guard to see the reset take effect right away; state setters
    // alone don't do that (they only apply on the next render), so a reset()-then-loadNextPage()
    // call would otherwise still read the pre-reset pageNumber/totalRecords and could silently
    // no-op if the previous view had already loaded everything.
    const pageNumberRef = useRef<number>(0);
    const totalRecordsRef = useRef<number>(0);
    const loadingRef = useRef<boolean>(false);

    const hasMore = pageNumberRef.current === 0 || pageNumberRef.current * pageSize < totalRecordsRef.current;

    const loadNextPage = useCallback(async () => {
        if (loadingRef.current || (pageNumberRef.current > 0 && pageNumberRef.current * pageSize >= totalRecordsRef.current)) {
            return;
        }
        const nextPage = pageNumberRef.current + 1;
        loadingRef.current = true;
        setLoading(true);
        try {
            const result = await fetchPage(nextPage);
            setItems((prev) => (nextPage === 1 ? result.items : [...prev, ...result.items]));
            totalRecordsRef.current = result.totalRecords;
            setTotalRecords(result.totalRecords);
            pageNumberRef.current = nextPage;
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchPage, pageSize]);

    const onScroll = (event: UIEvent<HTMLElement>) => {
        if (checkScrollStatus(event.currentTarget as HTMLDivElement)) {
            loadNextPage();
        }
    };

    // Call when the underlying query changes (new search, new sort, new selected report) so the
    // next loadNextPage() starts a fresh page 1 instead of appending onto stale results.
    const reset = () => {
        pageNumberRef.current = 0;
        totalRecordsRef.current = 0;
        loadingRef.current = false;
        setItems([]);
        setTotalRecords(0);
        setLoading(false);
    };

    return { items, loading, hasMore, totalRecords, onScroll, loadNextPage, reset };
}



// WEBPACK FOOTER //
// ./src/util/useInfiniteScroll.ts